import { IconDeviceFloppy } from "@tabler/icons-react"
import { Button, MultiSelect, Switch } from "@mantine/core"
import { Textarea } from "@mantine/core"
import { Modal, TextInput, Select } from "@mantine/core"
import React, { useEffect, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import dayjs from "dayjs"
import { RentIncrease } from "../../../models/rent-increase.model"
import * as yup from "yup"
import { yupResolver } from "@hookform/resolvers/yup"
import { IconCalendar } from "@tabler/icons-react"
import { DateInput, DatePickerInput } from "@mantine/dates"
import { useLazyGetUnitFloorsQuery } from "../store/lease.query"
import { CollectionQuery } from "../../../models/collection.model"
import {
  useLazyGetPropertiesForNotificationQuery,
  useLazyGetUnitTypesForNotificationsQuery,
} from "../../notification/store/notification.query"
import { useCurrentUser } from "../../../shared/hooks/current-user.hook"
import { useLazyGetUnitsOfPropertyQuery } from "../../property/store/property.query"
import LoadingInputWrapper from "../../../shared/component/LoadingInputWrapper"
import { useCreateBulkRentIncreaseMutation } from "../store/rent-increase.query"

type Props = {
  open: any
  opened: boolean
  close: any
}
export default function BulkRentIncreaseComponent({
  open,
  opened,
  close,
}: Props) {
  const { t } = useTranslation()
  const [type, setType] = useState<"Amount" | "Percent">("Amount")
  const [selectedProperties, setSelectedProperties] = useState<string[]>([])
  const [selectedUnits, setSelectedUnits] = useState<string[]>([])
  const [unitSearch, setUnitSearch] = useState<string | null>(null)
  const [unitSearchValue, setUnitSearchValue] = useState<string>("")
  const [cachedUnits, setCachedUnits] = useState<Map<string, any>>(new Map())
  const [selectedFloor, setSelectedFloor] = useState<string[]>([])
  const [selectedUnitTypes, setSelectedUnitTypes] = useState<string[]>([])
  const [startDateRange, setStartDateRange] = useState<
    [Date | null, Date | null]
  >([null, null])
  const [endDateRange, setEndDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ])
  const [userInfo, userId, ownerInfo] = useCurrentUser()

  const [propertyCollection, setPropertyCollection] = useState<CollectionQuery>(
    {
      skip: 0,
      top: 100,
      orderBy: [{ field: "createdAt", direction: "desc" }],
      // includes: ["accountReceiver"],
    },
  )
  const [unitCollection, setUnitCollection] = useState<CollectionQuery>({
    skip: 0,
    top: 100,
    orderBy: [{ field: "createdAt", direction: "desc" }],
    // includes: ["accountReceiver"],
  })

  const [createBulkRentIncrease, createBulkRentIncreaseResponse] =
    useCreateBulkRentIncreaseMutation()
  const [getProperties, properties] = useLazyGetPropertiesForNotificationQuery()
  const [getUnits, units] = useLazyGetUnitsOfPropertyQuery()
  const [getUnitTypes, unitTypes] = useLazyGetUnitTypesForNotificationsQuery()

  const [getUnitFloors, unitFloors] = useLazyGetUnitFloorsQuery()
  useEffect(() => {
    getUnitFloors({})
  }, [])

  useEffect(() => {
    if (units.data?.data) {
      setCachedUnits((prev) => {
        const newCache = new Map(prev)
        units.data?.data.forEach((unit: any) => {
          newCache.set(unit.id, unit)
        })
        return newCache
      })
    }
  }, [units.data])

  useEffect(() => {
    const handler = setTimeout(() => {
      setUnitSearch(unitSearchValue || null)
    }, 500)

    return () => {
      clearTimeout(handler)
    }
  }, [unitSearchValue])
  const defaultValue: RentIncrease = {
    //   increaseDate: null,
    increaseAmount: 0,
    increasePercentage: 0,
    reasonForIncrease: "",
    increaseDate: null,
    notifyTenant: false,
    // nextIncreaseDate: null,
    notes: "",
  }

  let schema: any = yup.object().shape({
    increaseAmount: yup.number().when([], {
      is: () => type == "Amount",
      then: (schema) =>
        schema
          .typeError(
            t("typeValidation", {
              field: t("increaseAmount"),
              type: t("number"),
            }),
          )
          .required(t("requiredValidation", { field: t("increaseAmount") })),
    }),
    increasePercentage: yup.number().when([], {
      is: () => type == "Percent",
      then: (schema) =>
        schema
          .typeError(
            t("typeValidation", {
              field: t("increasePercent"),
              type: t("number"),
            }),
          )
          .required(t("requiredValidation", { field: t("increasePercent") })),
    }),
    reasonForIncrease: yup
      .string()
      .typeError(
        t("typeValidation", {
          field: t("reasonForIncrease"),
          type: t("string"),
        }),
      )
      .required(t("requiredValidation", { field: t("reasonForIncrease") })),
    increaseDate: yup
      .date()
      .typeError(
        t("typeValidation", { field: t("increaseDate"), type: t("date") }),
      )
      .required(t("requiredValidation", { field: t("increaseDate") })),
    // increaseDate: yup.date().typeError("Increase date should be a date").required("Increase date is required"),
    notes: yup
      .string()
      .typeError(t("requiredValidation", { field: t("notes") })),
  })

  function closeModal() {
    reset(defaultValue)
    setStartDateRange([null, null])
    setEndDateRange([null, null])
    setSelectedProperties([])
    setSelectedUnits([])
    setSelectedUnitTypes([])
    close()
  }

  const {
    register,
    setValue,
    getValues,
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<RentIncrease>({
    resolver: yupResolver(schema),
    mode: "all",
  })

  function onSubmit(data: any) {
    try {
      data = {
        ...data,
        increaseDate: dayjs(data?.increaseDate).format("YYYY-MM-DD"),
        // propertyIds: selectedProperties,
        // unitIds: selectedUnits,
        // startDate: startDateRange?.map((date) => dayjs(date).format("YYYY-MM-DD")),
        // endDate: endDateRange?.map((date) => dayjs(date).format("YYYY-MM-DD")),
      }

      if (selectedProperties && selectedProperties?.length > 0) {
        data = { ...data, propertyIds: selectedProperties }
      }

      if (selectedUnits && selectedUnits?.length > 0) {
        data = { ...data, unitIds: selectedUnits }
      }

      if (selectedUnitTypes && selectedUnitTypes?.length > 0) {
        data = { ...data, unitTypes: selectedUnitTypes }
      }

      if (
        startDateRange &&
        startDateRange?.length == 2 &&
        startDateRange[0] &&
        startDateRange[1]
      ) {
        data = {
          ...data,
          startDate: startDateRange?.map((date) =>
            dayjs(date).format("YYYY-MM-DD"),
          ),
        }
      }
      if (selectedFloor && selectedFloor?.length > 0) {
        data = { ...data, floor: selectedFloor }
      }

      if (
        endDateRange &&
        endDateRange?.length == 2 &&
        endDateRange[0] &&
        endDateRange[1]
      ) {
        data = {
          ...data,
          endDate: endDateRange?.map((date) =>
            dayjs(date).format("YYYY-MM-DD"),
          ),
        }
      }

      createBulkRentIncrease(data).then((response: any) => {
        closeModal()
      })
    } catch (e: any) {
      console.log("🚀 ~ onSubmit ~ e:", e)
      closeModal()
    }
  }
  const onError = (error: any) => {
    console.log("🚀 ~ onError ~ error:", error)
  }

  useEffect(() => {
    if (userInfo?.role?.key === "owner") {
      getProperties({
        ...propertyCollection,
        filter: [
          [
            {
              field: "ownerId",
              operator: "=",
              value: ownerInfo?.parentOwnerId,
            },
          ],
        ],
      })
    } else {
      getProperties(propertyCollection)
    }
  }, [propertyCollection])
  useEffect(() => {
    if (selectedProperties && selectedProperties.length > 0 && unitSearch) {
      getUnits({
        ...unitCollection,
        filter: [
          [{ field: "name", operator: "like", value: unitSearch }],
          [{ field: "propertyId", operator: "in", value: selectedProperties }],
          [{ field: "enabled", operator: "=", value: true }],
        ],
      })
    }

    // else {
    //   getUnits({
    //     ...unitCollection,
    //     filter: [
    //       [{ field: "propertyId", operator: "in", value: selectedProperties }],
    //       [{ field: "enabled", operator: "=", value: true }],
    //     ],
    //   })
    // }
  }, [unitSearch, selectedProperties])

  useEffect(() => {
    getUnitTypes({})
  }, [])
  function _parseProperties() {
    const data: { label: string; value: string }[] = []

    properties.data?.data?.map((property: any) => {
      data.push({ label: property.name, value: property.id })
    })

    return data
  }
  function _parseUnits() {
    const data: { label: string; value: string }[] = []
    const currentUnitIds = new Set(
      units.data?.data?.map((u: any) => u.id) || [],
    )

    // Add currently fetched units
    units.data?.data?.map((unit: any) => {
      data.push({ label: unit.name, value: unit.id })
    })

    // Add selected units that are in cache but not in current results
    selectedUnits.forEach((selectedId) => {
      if (!currentUnitIds.has(selectedId) && cachedUnits.has(selectedId)) {
        const cachedUnit = cachedUnits.get(selectedId)
        data.push({ label: cachedUnit.name, value: cachedUnit.id })
      }
    })

    return data
  }
  function _parseFloors() {
    const data: { label: string; value: string }[] = []

    unitFloors?.data?.map((floor: any) => {
      data.push({ label: floor, value: floor })
    })
    // console.log("🚀 ~ unitFloors.data?.data?.map ~ unitFloors:", unitFloors.data)

    return data
  }

  function _parseUnitTypes() {
    const data: { label: string; value: string }[] = []

    unitTypes?.data?.map((type: any) => {
      data.push({ label: type, value: type })
    })
    return data
  }

  return (
    <Modal
      opened={opened}
      onClose={closeModal}
      size={"md"}
      title={t("rentIncreaseAction", { action: t("add") })}
      centered
    >
      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <div className="flex gap-8 w-full mt-20 space-x-4 justify-between ">
          <div className="w-full">
            {/* <div className="flex w-full space-x-4  justify-center"> */}
            <MultiSelect
              radius={"xs"}
              classNames={{ label: "dark:text-white" }}
              className="w-full"
              clearable
              // error={errors?.method && `${errors?.method?.message}`}
              label={t("property", { count: 2 })}
              placeholder={t("property", { count: 2 })}
              data={_parseProperties()}
              // withAsterisk
              value={selectedProperties}
              onChange={(value) => setSelectedProperties(value)}

              // {...field}
            />

            <LoadingInputWrapper isLoading={units.isFetching}>
              <MultiSelect
                radius={"xs"}
                classNames={{ label: "dark:text-white" }}
                className="w-full"
                searchable
                description={
                  "Please select at least 1 property before selecting a unit"
                }
                clearable
                dropdownPosition="bottom"
                // error={errors?.method && `${errors?.method?.message}`}
                label={t("unit", { count: 2 })}
                placeholder={t("unit", { count: 2 })}
                searchValue={unitSearchValue}
                onSearchChange={setUnitSearchValue}
                data={_parseUnits()}
                // withAsterisk
                value={selectedUnits}
                onChange={(value) => {
                  setSelectedUnits(value)
                  setUnitSearchValue("")
                }}

                // {...field}
              />
            </LoadingInputWrapper>
            <LoadingInputWrapper isLoading={unitFloors.isFetching}>
              <MultiSelect
                radius={"xs"}
                classNames={{ label: "dark:text-white" }}
                className="w-full"
                clearable
                dropdownPosition="bottom"
                // error={errors?.method && `${errors?.method?.message}`}
                label={t("floor", { count: 2 })}
                placeholder={t("floor", { count: 2 })}
                data={_parseFloors()}
                // withAsterisk
                value={selectedFloor}
                onChange={(value) => setSelectedFloor(value)}

                // {...field}
              />
            </LoadingInputWrapper>

            {selectedUnits?.length < 1 && (
              <>
                <DatePickerInput
                  className="w-full text-sm"
                  dropdownType="modal"
                  type="range"
                  //@ts-ignore
                  placeholder="Start Date"
                  label="Start Date"
                  allowSingleDateInRange
                  value={startDateRange}
                  onChange={setStartDateRange}
                  maw={400}
                />
                <DatePickerInput
                  className="w-full text-sm"
                  dropdownType="modal"
                  type="range"
                  //@ts-ignore
                  placeholder="End Date"
                  label="End Date"
                  allowSingleDateInRange
                  value={endDateRange}
                  onChange={setEndDateRange}
                  maw={400}
                />
              </>
            )}

            <MultiSelect
              radius={"xs"}
              classNames={{ label: "dark:text-white" }}
              className="w-full"
              clearable
              dropdownPosition="bottom"
              // error={errors?.method && `${errors?.method?.message}`}
              label={"Unit Types"}
              placeholder={"Unit Types"}
              data={_parseUnitTypes()}
              // withAsterisk
              value={selectedUnitTypes}
              onChange={(value) => setSelectedUnitTypes(value)}

              // {...field}
            />

            {/* </div> */}
            <Select
              classNames={{ label: "dark:text-white" }}
              className="w-full"
              clearable
              dropdownPosition="bottom"
              label={t("increaseType")}
              placeholder={t("increaseType")}
              data={[
                { value: "Amount", label: t("amount") },
                { value: "Percent", label: t("percent") },
              ]}
              value={type}
              onChange={(value: "Amount" | "Percent") => setType(value)}
            />

            {type == "Amount" ? (
              <TextInput
                classNames={{ label: "dark:text-white" }}
                className="w-full"
                type="number"
                error={
                  errors.increaseAmount && `${errors?.increaseAmount?.message}`
                }
                withAsterisk
                label={t("increaseAmount")}
                placeholder={t("increaseAmount")}
                {...register("increaseAmount")}
              />
            ) : (
              <TextInput
                classNames={{ label: "dark:text-white" }}
                className="w-full"
                type="number"
                error={
                  errors.increasePercentage &&
                  `${errors?.increasePercentage?.message}`
                }
                withAsterisk
                label={t("increasePercent")}
                placeholder={t("increasePercent")}
                {...register("increasePercentage")}
              />
            )}

            <TextInput
              classNames={{ label: "dark:text-white" }}
              className="w-full"
              // type="number"
              error={
                errors.reasonForIncrease &&
                `${errors?.reasonForIncrease?.message}`
              }
              withAsterisk
              label={t("reasonForIncrease")}
              placeholder={t("reasonForIncrease")}
              {...register("reasonForIncrease")}
            />

            <Controller
              control={control}
              name="increaseDate"
              render={({ field }) => (
                <DateInput
                  icon={<IconCalendar size={16} />}
                  className="w-full mt-3"
                  {...field}
                  // excludeDate={(edate) => {
                  //   if (edate.getFullYear() < date.getFullYear()) {
                  //     return true;
                  //   } else if (
                  //     edate.getFullYear() === date.getFullYear() &&
                  //     edate.getMonth() < date.getMonth()
                  //   ) {
                  //     return true;
                  //   } else {
                  //     if (
                  //       edate.getFullYear() <= date.getFullYear() &&
                  //       edate.getMonth() === date.getMonth() &&
                  //       edate.getDate() <= date.getDate()
                  //     ) {
                  //       return true;
                  //     } else {
                  //       return false;
                  //     }
                  //   }
                  // }}
                  error={
                    errors.increaseDate && `${errors.increaseDate?.message}`
                  }
                  label={t("increaseDate")}
                  withAsterisk
                />
              )}
            />

            <div className="my-3">
              <Controller
                control={control}
                name="notifyTenant"
                render={({ field }) => (
                  //@ts-ignore
                  <Switch
                    className={"my-1"}
                    checked={field.value}
                    // {...field}
                    onChange={(event: any) =>
                      field.onChange(event.currentTarget.checked)
                    }
                    label="Should we notify the tenant?"
                  />
                )}
              />
            </div>

            <Textarea
              classNames={{ label: "dark:text-white" }}
              className="w-full"
              error={errors.notes && `${errors?.notes?.message}`}
              // withAsterisk
              label={t("notes")}
              placeholder={t("notes")}
              {...register("notes")}
            />
          </div>
        </div>

        <div className="pt-8 flex justify-end">
          <Button
            variant="filled"
            className="bg-primary text-white"
            type="submit"
            classNames={{ label: "flex space-x-1" }}
            size={"xs"}
            loading={createBulkRentIncreaseResponse.isLoading}
            //  onClick={() => handleAssignDriver()}
          >
            <span>
              <IconDeviceFloppy size={22} strokeWidth={1.5} />
            </span>
            {t("submit")}
          </Button>
        </div>
      </form>
    </Modal>
  )
}
